/*
 * Shailah shop by color. One script for the color page, the homepage strip, the
 * collection bar and the two product page blocks (about this color; similar colors).
 * No dependencies.
 *
 * Data: <collection>?view=sbc-data (in-stock products, best sellers first, see
 * templates/collection.sbc-data.liquid) and assets/sbc-colors.json (verified color
 * family per product plus the color-name map used for products not yet verified) and
 * assets/sbc-meanings.json (Shailah's own copy about each color family and shade group).
 * Every surface shows one product category (config "category": shayla, jalabiya, abaya,
 * kaftan or dress; blank = all), so new categories reuse this code unchanged.
 * Analytics events fire only on the live theme, never in a theme preview.
 */
(() => {
  'use strict';

  if (window.__sbcBooted) return; // every sbc section includes this file
  window.__sbcBooted = true;

  const TTL = 10 * 60 * 1000;
  const MAX_PAGES = 12;
  const LIVE = Boolean(window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main');
  const THEME_ID = (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || 0;
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const memo = new Map();

  // ---------- helpers ----------

  const el = (tag, className) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  };

  const readJSON = (root, selector) => {
    const node = root.querySelector(selector);
    return node ? JSON.parse(node.textContent) : null;
  };

  // Same rules as norm() in build_colors.py: drop zero-width and direction marks, Arabic
  // diacritics and tatweel, unify alef and ya forms, drop punctuation, collapse spaces, lower-case.
  const norm = (value) =>
    String(value || '')
      .normalize('NFKC')
      .replace(/[​-‏‪-‮⁠-⁩؜﻿]/g, '')
      .replace(/[ً-ٰٟـ]/g, '')
      .replace(/[أإآ]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/[،,.؛;:!?()"'«»\-_/]/g, ' ')
      .replace(/[\s\u0085\u001C-\u001F]+/g, ' ')
      .trim()
      .toLowerCase();

  const withQuery = (url, params) => {
    const target = new URL(url, window.location.origin);
    Object.entries(params).forEach(([key, value]) => target.searchParams.set(key, value));
    return target.pathname + target.search;
  };

  // No "Accept: application/json" header: Shopify then looks for its own JSON format of the
  // collection and answers 404 instead of rendering the ?view=sbc-data template.
  const fetchJSON = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
    return response.json();
  };

  // Low-priority work that should still run in a background or pre-rendered tab.
  const whenIdle = (run, timeout = 2500) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout });
    else window.setTimeout(run, 600);
  };

  // wa.me link with a prefilled message; the number comes from the section settings.
  const waHref = (cfg, text) => `https://wa.me/${String(cfg.wa || '').replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template || '');

  const track = (name, params) => {
    const payload = Object.assign({ language: document.documentElement.lang || '' }, params);
    if (!LIVE) {
      if (window.console && console.debug) console.debug('[sbc]', name, payload);
      return;
    }
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(`sbc_${name}`, payload);
      }
    } catch (error) {
      /* analytics must never break shopping */
    }
    try {
      if (typeof window.gtag === 'function') window.gtag('event', `sbc_${name}`, payload);
    } catch (error) {
      /* same */
    }
  };

  const countText = (cfg, n) => {
    let category = 'other';
    try {
      category = new Intl.PluralRules(cfg.lang === 'ar' ? 'ar' : 'en').select(n);
    } catch (error) {
      category = n === 1 ? 'one' : 'other';
    }
    const template = cfg.s[`count_${category}`] || cfg.s.count_other;
    return template.replace('{n}', String(n));
  };

  // ---------- data ----------

  const loadItems = (source) => {
    if (memo.has(source)) return memo.get(source);
    const key = `sbc:v2:${THEME_ID}:${source}`;
    const job = (async () => {
      try {
        const hit = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (hit && Date.now() - hit.t < TTL && Array.isArray(hit.items)) return hit.items;
      } catch (error) {
        /* storage can be unavailable; fetch instead */
      }
      const base = { view: 'sbc-data', 'filter.v.availability': '1', sort_by: 'best-selling' };
      // In a theme preview, background requests can be answered by the live theme (which has no
      // sbc-data view), so name the previewed theme explicitly. Never needed on the live theme.
      if (!LIVE && THEME_ID) base.preview_theme_id = String(THEME_ID);
      const first = await fetchJSON(withQuery(source, Object.assign({ page: '1' }, base)));
      const pages = Math.min(first.pages || 1, MAX_PAGES);
      if ((first.pages || 1) > MAX_PAGES && window.console) {
        console.warn(`[sbc] ${first.pages} pages of products, only the first ${MAX_PAGES} were read`);
      }
      const rest = [];
      for (let page = 2; page <= pages; page += 1) {
        rest.push(fetchJSON(withQuery(source, Object.assign({ page: String(page) }, base))));
      }
      const byId = new Map();
      [first].concat(await Promise.all(rest)).forEach((chunk) => {
        (chunk.items || []).forEach((item) => {
          if (!byId.has(item.i)) byId.set(item.i, item);
        });
      });
      const items = Array.from(byId.values());
      try {
        window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), items }));
      } catch (error) {
        /* quota or privacy mode: keep going without the cache */
      }
      return items;
    })();
    memo.set(source, job);
    job.catch(() => memo.delete(source));
    return job;
  };

  const loadColors = (url) => {
    if (!memo.has(url)) {
      const job = fetchJSON(url);
      memo.set(url, job);
      job.catch(() => memo.delete(url));
    }
    return memo.get(url);
  };

  // Arabic: first occurrence (base color first). English: last occurrence (base color last),
  // as a whole word with an optional plural or -ish/-y ending ("reddish" is red, "embroidered"
  // and "primrose" are not red or rose).
  const findWord = (key, word, arabic) => {
    if (arabic) return key.indexOf(word);
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|[^a-z])${escaped}(?:s|y|ish|dish)?(?![a-z])`, 'g');
    let at = -1;
    let match = pattern.exec(key);
    while (match) {
      at = match.index + match[1].length;
      match = pattern.exec(key);
    }
    return at;
  };

  // Families a mislabeled color option can plausibly belong to (neighbouring shades).
  const NEAR = {
    black: ['navy', 'grey'],
    white: ['beige', 'grey', 'yellow'],
    beige: ['white', 'brown', 'green', 'orange', 'yellow', 'grey', 'pink'],
    grey: ['white', 'beige', 'brown', 'blue', 'navy', 'green', 'purple', 'black'],
    brown: ['beige', 'orange', 'grey', 'red'],
    pink: ['orange', 'red', 'purple', 'beige'],
    red: ['pink', 'orange', 'purple', 'brown'],
    orange: ['beige', 'yellow', 'pink', 'red', 'brown'],
    yellow: ['beige', 'orange', 'green', 'white'],
    green: ['turquoise', 'beige', 'yellow', 'grey'],
    turquoise: ['blue', 'green'],
    blue: ['navy', 'turquoise', 'grey', 'purple'],
    navy: ['blue', 'purple', 'black', 'grey'],
    purple: ['pink', 'red', 'navy', 'blue', 'grey'],
  };

  // Family entries for one product. Verified assignments win; then the product's color
  // option values; then its color metafields; then color words in the metafield text.
  const makeResolver = (colors, lang) => {
    const names = colors.names || {};
    const phrases = colors.phrases || [];
    const words = colors.words || [];
    const verified = colors.products || {};
    const variantColors = colors.variants || {};
    // The Shailah palette (Owner ruling: products carry palette names only, so they are linked):
    // slug -> [family, Arabic name, English name]; palette names -> slug; each family's base color.
    const palette = colors.palette || {};
    const base = colors.family_base || {};
    const bySlugName = {};
    Object.entries(palette).forEach(([slug, [fam, ar, en]]) => {
      bySlugName[norm(ar)] = bySlugName[norm(ar)] || slug;
      bySlugName[norm(en)] = bySlugName[norm(en)] || slug;
    });
    // Same resolution as family_of() in build_colors.py: exact name, then multi-word phrase,
    // then single words. Arabic puts the base color first («بيج خوخي» is beige), English puts
    // it last ("peach beige" is beige); ties keep list order.
    const familiesOf = (text) => {
      const key = norm(text);
      if (!key) return null;
      if (names[key]) return names[key];
      const phrase = phrases.find(([p]) => key.includes(p));
      if (phrase) return [phrase[1]];
      const arabic = /[\u0600-\u06FF]/.test(key);
      let best = null;
      words.forEach(([word, fam]) => {
        const at = findWord(key, word, arabic);
        if (at < 0) return;
        const rank = arabic ? at : -at;
        if (!best || rank < best.rank) best = { rank, fam };
      });
      return best ? [best.fam] : null;
    };
    // Every entry links to one palette color and shows its palette name: a verified product's
    // own link for its main color; else a label that IS a palette name in that family; else the
    // family's base palette color. Free-text store labels are never shown as color names.
    const link = (product, entry) => {
      const known = verified[product.i];
      let slug = entry.slug;
      if (!slug) {
        const named = bySlugName[norm(entry.shade)];
        if (known && known[4] && entry.fam === known[0] && !entry.secondary) slug = known[4];
        else if (named && palette[named] && palette[named][0] === entry.fam) slug = named;
        else slug = base[entry.fam];
      }
      const color = palette[slug];
      return Object.assign(entry, { slug, shade: color ? (lang === 'ar' ? color[1] : color[2]) : entry.shade });
    };
    return (product) => resolveRaw(product).map((entry) => link(product, entry));
    function resolveRaw(product) {
      const entries = [];
      const push = (fam, extra) => {
        if (fam && !entries.some((entry) => entry.fam === fam)) entries.push(Object.assign({ fam }, extra));
      };
      const known = verified[product.i];
      // In-stock color options of a multi-color listing (product.o holds only in-stock ones).
      // A photo-identified variant color (colors.variants, for options named by photo position
      // such as «يسار TL-01») is exact and wins over the option label.
      const options = (Array.isArray(product.o) ? product.o : [])
        .map(([value, variant, image]) => {
          const exact = variantColors[variant];
          return exact
            ? { value, variant, image, fams: [exact[0]], exact: true, shade: lang === 'ar' ? exact[1] : exact[2], slug: exact[3] }
            : { value, variant, image, fams: familiesOf(value) };
        })
        .filter((option) => option.fams);
      if (options.length && options.every((option) => option.exact)) {
        options.forEach((option) => push(option.fams[0], { shade: option.shade, slug: option.slug, secondary: false, variant: option.variant, image: option.image }));
        return entries;
      }
      if (known) {
        const verifiedFams = [known[0], known[1]].filter(Boolean);
        const shade = (lang === 'ar' ? known[2] : known[3]) || '';
        if (options.length) {
          // The in-stock options decide which verified colors are buyable now, so a sold-out
          // color never shows. An option whose store label the photo check rejected (e.g.
          // «زيتوني فاتح» on a khaki variant) takes the verified color left over: the only one
          // left, else the photo's main color. With none left, the photo never showed that
          // variant, so its own label is trusted.
          const unmatched = [];
          const spare = verifiedFams.slice();
          options.forEach((option) => {
            const fam = option.fams[0];
            if (verifiedFams.includes(fam)) {
              push(fam, { shade: option.value, secondary: fam !== known[0], variant: option.variant, image: option.image });
              if (spare.includes(fam)) spare.splice(spare.indexOf(fam), 1);
            } else {
              unmatched.push(option);
            }
          });
          unmatched.forEach((option) => {
            // A mislabel is a neighbouring shade (olive for khaki, mint for aqua); a far-off
            // label is a variant the photo never showed, so its own label is trusted.
            const near = spare.filter((fam) => (NEAR[option.fams[0]] || []).includes(fam));
            let target = option.fams[0];
            if (near.length === 1) target = near[0];
            else if (near.includes(known[0])) target = known[0];
            if (spare.includes(target)) spare.splice(spare.indexOf(target), 1);
            push(target, {
              // never show a label the photo check rejected as a shade name
              shade: target === known[0] ? shade : target === option.fams[0] ? option.value : '',
              secondary: target !== known[0],
              variant: option.variant,
              image: option.image,
            });
          });
          if (entries.length) return entries;
        }
        push(known[0], { shade, secondary: false });
        push(known[1], { shade, secondary: true });
        return entries;
      }
      if (options.length) {
        options.forEach((option) => push(option.fams[0], { shade: option.value, secondary: false, variant: option.variant, image: option.image }));
        return entries;
      }
      const colorsText = product.c || [];
      for (const text of colorsText) {
        const fams = familiesOf(text);
        if (fams) {
          const shade = lang === 'ar' ? colorsText[0] || '' : colorsText[1] || colorsText[0] || '';
          push(fams[0], { shade, secondary: false });
          push(fams[1], { shade, secondary: true });
          break;
        }
      }
      return entries;
    }
  };

  // The palette color of a resolved entry as [Arabic name, English name, [], slug], or null.
  const paletteOf = (colors, entry) => {
    const color = entry && entry.slug ? ((colors && colors.palette) || {})[entry.slug] : null;
    return color && color[0] === entry.fam ? [color[1], color[2], [], entry.slug] : null;
  };

  // Copy for one color: its palette color first, the family as the fallback (assets/sbc-meanings.json).
  const meaningOf = (meanings, fam, group, lang) => {
    if (!meanings) return null;
    const family = (meanings.families || {})[fam];
    const shade = group ? (meanings.palette || {})[group[3]] : null;
    const side = (entry) => (entry ? entry[lang === 'ar' ? 'ar' : 'en'] || entry.ar : null);
    const f = side(family) || {};
    const g = shade && shade.family === fam ? side(shade) || {} : {};
    return {
      about: g.about || f.about || '',
      meaning: g.meaning || f.meaning || '',
      wear: f.wear || '',
      pairs: (family && family.pairs) || [],
    };
  };

  // fam -> [{ p, fam, shade, secondary, variant, image }], primary matches first.
  const buildIndex = (items, colors, cfg) => {
    const resolve = makeResolver(colors, cfg.lang);
    const verified = colors.products || {};
    const index = new Map();
    items.forEach((product) => {
      if (cfg.category && (product.g || 'shayla') !== cfg.category) return; // one category per surface
      const known = verified[product.i];
      resolve(product).forEach((entry) => {
        // Arabic and English shade names together, for the shade chips.
        const shadeText = known ? `${known[2] || ''} ${known[3] || ''}` : `${entry.shade || ''} ${(product.c || []).join(' ')}`;
        if (!index.has(entry.fam)) index.set(entry.fam, []);
        index.get(entry.fam).push(Object.assign({ p: product, shadeText }, entry));
      });
    });
    index.forEach((list) => list.sort((a, b) => Number(a.secondary) - Number(b.secondary)));
    return index;
  };

  // ---------- product card (same markup as snippets/shailah-product-card.liquid) ----------

  const srcset = (url) =>
    [270, 360, 540, 720].map((w) => `${url.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');

  const buildCard = (entry, cfg, context) => {
    const product = entry.p;
    const url = entry.variant ? withQuery(product.u, { variant: String(entry.variant) }) : product.u;
    const image = entry.image || product.m;
    const card = el('article', 'sh-product sbc-card');

    const media = el('a', 'sh-product-image');
    media.href = url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (image) {
      const img = el('img');
      img.src = image;
      img.srcset = srcset(image);
      img.sizes = '(max-width: 749px) 46vw, (max-width: 989px) 30vw, 22vw';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = '';
      img.width = 540;
      img.height = 675;
      media.append(img);
    }
    card.append(media);

    if (product.q === 1 || product.q === 2) {
      const badge = el('span', 'sbc-badge');
      badge.textContent = product.q === 1 ? cfg.s.last_one : cfg.s.last_two;
      card.append(badge);
    }

    const title = el('h3');
    const link = el('a');
    link.href = url;
    link.textContent = product.t;
    title.append(link);
    card.append(title);

    const price = el('span', 'sh-price');
    price.textContent = (product.f ? `${cfg.s.from} ` : '') + product.p;
    card.append(price);

    if (product.d && product.v) {
      const form = el('form');
      form.method = 'post';
      form.action = cfg.cart_add;
      [['id', product.v], ['quantity', 1], ['form_type', 'product'], ['utf8', '✓']].forEach(([name, value]) => {
        const input = el('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        form.append(input);
      });
      const button = el('button', 'sh-product-action');
      button.type = 'submit';
      button.textContent = cfg.s.add;
      form.append(button);
      form.addEventListener('submit', () => track('add_to_cart', Object.assign({ product_id: product.i }, context)));
      card.append(form);
    } else {
      const action = el('a', 'sh-product-action');
      action.href = url;
      action.textContent = cfg.s.choose;
      card.append(action);
    }

    card.addEventListener('click', (event) => {
      if (event.target.closest('a')) track('item_click', Object.assign({ product_id: product.i }, context));
    });
    return card;
  };

  // ---------- color page ----------

  const initView = (root) => {
    const cfg = readJSON(root, 'script[data-sbc-config]');
    const buttons = Array.from(root.querySelectorAll('.sbc-picker .sbc-sw'));
    const bar = root.querySelector('.sbc-picker-bar');
    const summary = root.querySelector('.sbc-summary');
    const shadesBox = root.querySelector('.sbc-shades');
    const results = root.querySelector('.sbc-results');
    const note = root.querySelector('.sbc-color-note');
    const noteText = root.querySelector('.sbc-color-meaning');
    const noteAbout = root.querySelector('.sbc-color-about');
    const noteWear = root.querySelector('.sbc-color-wear');
    const notePairs = root.querySelector('.sbc-color-pairs');
    const ask = root.querySelector('[data-sbc-ask]');
    const notifyTemplate = root.querySelector('template[data-sbc-notify-template]');
    const familyName = Object.fromEntries(cfg.families.map((family) => [family.k, family.n]));
    const isFamily = (fam) => Object.prototype.hasOwnProperty.call(familyName, fam);
    const heading = root.querySelector('.sbc-intro h1');
    const baseTitle = document.title;
    // shadeSlug: a palette slug from ?shade= (language-neutral, so a language switch keeps the chosen shade), applied once counts exist
    const state = { fam: null, shade: '', shadeSlug: '', index: null, colors: null, meanings: null, failed: false };

    // The chosen color explained: what it is, what it expresses, when to wear it, and which
    // jalabiya or dress colors it goes with; plus a WhatsApp question prefilled with it.
    const renderNote = () => {
      if (!note) return;
      const copy = meaningOf(state.meanings, state.fam, null, cfg.lang) || {};
      [[noteAbout, copy.about], [noteText, copy.meaning], [noteWear, copy.wear]].forEach(([node, text]) => {
        if (!node) return;
        node.textContent = text || '';
        node.hidden = !node.textContent;
      });
      if (notePairs) {
        const list = notePairs.querySelector('.sbc-color-pairs-list');
        list.replaceChildren(
          ...(copy.pairs || []).filter((fam) => familyName[fam]).map((fam) => {
            const chip = el('span', 'sbc-chip sbc-chip--static');
            const swatch = el('span', `sbc-dot sbc-dot--sm sbc-dot--${fam}`);
            swatch.setAttribute('aria-hidden', 'true');
            chip.append(swatch, document.createTextNode(familyName[fam]));
            return chip;
          })
        );
        notePairs.hidden = !list.children.length;
      }
      if (ask) {
        ask.href = waHref(cfg, `${fill(cfg.s.ask_text_color, { color: familyName[state.fam] || '' })}\n${window.location.href}`);
      }
      const guideLink = root.querySelector('[data-sbc-guide-link]');
      if (guideLink) guideLink.href = withQuery(cfg.all, { view: 'color-guide', color: state.fam });
      note.hidden = false;
    };

    // "Tell me when this color is back": the customer form, tagged with the color.
    const notifyBox = () => {
      if (!notifyTemplate) return null;
      const box = notifyTemplate.content.firstElementChild.cloneNode(true);
      const tags = box.querySelector('[data-sbc-notify-tags]');
      if (tags) tags.value = `${tags.dataset.base},sbc-notify-color-${state.fam}`;
      box.addEventListener('submit', () => track('notify_request', { color: state.fam, source: 'color_page' }));
      return box;
    };

    const setPressed = () =>
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.family === state.fam)));

    const skeleton = () => {
      const grid = el('div', 'sh-products sbc-grid');
      for (let i = 0; i < (DESKTOP.matches ? 8 : 4); i += 1) {
        const block = el('div', 'sbc-skel');
        block.setAttribute('aria-hidden', 'true');
        grid.append(block);
      }
      results.replaceChildren(grid);
      summary.textContent = cfg.s.loading;
    };

    const showError = () => {
      const box = el('div', 'sbc-message');
      const text = el('p');
      text.textContent = cfg.s.error;
      const retry = el('button', 'sh-button sh-button--outline');
      retry.type = 'button';
      retry.textContent = cfg.s.retry;
      retry.addEventListener('click', async () => {
        await load();
        results.focus({ preventScroll: true });
      });
      box.append(text, retry);
      summary.textContent = '';
      results.replaceChildren(box);
    };

    const renderEmpty = () => {
      const box = el('div', 'sbc-message');
      const text = el('p');
      text.textContent = cfg.s.empty;
      const alternatives = el('div', 'sbc-alternatives');
      const ranked = buttons
        .map((button) => ({ fam: button.dataset.family, n: (state.index.get(button.dataset.family) || []).length }))
        .filter((item) => item.n > 0 && item.fam !== state.fam)
        .sort((a, b) => b.n - a.n)
        .slice(0, 4);
      ranked.forEach(({ fam }) => {
        const chip = el('button', 'sbc-chip');
        chip.type = 'button';
        const dot = el('span', `sbc-dot sbc-dot--sm sbc-dot--${fam}`);
        dot.setAttribute('aria-hidden', 'true');
        chip.append(dot, document.createTextNode(familyName[fam]));
        chip.addEventListener('click', () => choose(fam, 'empty_state'));
        alternatives.append(chip);
      });
      box.append(text, alternatives);
      const notify = notifyBox();
      if (notify) box.append(notify);
      results.replaceChildren(box);
    };

    // Shade chips: the curated groups in sbc-colors.json ("shades"), only for colors with at
    // least 20 pieces and at least two groups of two or more.
    const shadeGroupOf = (entry) => paletteOf(state.colors, entry);
    const shadeOf = (entry) => {
      const hit = shadeGroupOf(entry);
      return hit ? (cfg.lang === 'ar' ? hit[0] : hit[1]) : '';
    };

    const renderShades = (list) => {
      const counts = new Map();
      const englishOf = new Map();
      const slugOf = new Map();
      if (list.length >= 20) {
        list.forEach((entry) => {
          const hit = shadeGroupOf(entry);
          if (!hit) return;
          const label = cfg.lang === 'ar' ? hit[0] : hit[1];
          counts.set(label, (counts.get(label) || 0) + 1);
          englishOf.set(label, hit[1]);
          if (hit[3]) slugOf.set(label, hit[3]);
        });
      }
      if (state.shadeSlug) {
        const wanted = state.shadeSlug;
        state.shadeSlug = '';
        slugOf.forEach((slug, label) => { if (slug === wanted && (counts.get(label) || 0) >= 2) state.shade = label; });
      }
      const keepShadeInUrl = (key) => {
        const url = new URL(window.location.href);
        if (key && slugOf.get(key)) url.searchParams.set('shade', slugOf.get(key)); else url.searchParams.delete('shade');
        window.history.replaceState(window.history.state, '', url);
      };
      const shades = Array.from(counts.entries())
        .filter(([, n]) => n >= 2)
        .sort((a, b) => b[1] - a[1]);
      if (shades.length < 2) {
        shadesBox.hidden = true;
        shadesBox.replaceChildren();
        state.shade = '';
        return;
      }
      if (state.shade && !counts.has(state.shade)) state.shade = '';
      const chip = (key, text) => {
        const button = el('button', 'sbc-chip');
        button.type = 'button';
        button.dataset.key = key;
        button.textContent = text;
        button.setAttribute('aria-pressed', String(state.shade === key));
        button.addEventListener('click', () => {
          state.shade = key;
          keepShadeInUrl(key);
          render();
          // the chips were rebuilt: put keyboard focus back on the one just used
          const again = Array.from(shadesBox.querySelectorAll('.sbc-chip')).find((b) => b.dataset.key === key);
          if (again) again.focus({ preventScroll: true });
          track('shade_select', { color: state.fam, shade: englishOf.get(key) || 'all' });
        });
        return button;
      };
      shadesBox.replaceChildren(chip('', cfg.s.all_shades), ...shades.map(([label, n]) => chip(label, `${label} (${n})`)));
      shadesBox.hidden = false;
    };

    // Each product joins the first configured collection (block order) that contains it;
    // configured collections sharing a key form one group; biggest groups are shown first.
    const renderGroups = (list) => {
      const groups = new Map();
      (cfg.groups || []).forEach((rule, order) => {
        if (!groups.has(rule.key)) groups.set(rule.key, { t: rule.t, order, items: [] });
      });
      const other = { t: cfg.s.other_group, order: Infinity, items: [] };
      list.forEach((entry) => {
        const ids = entry.p.k || [];
        const type = norm(entry.p.y);
        const rule = (cfg.groups || []).find(
          (candidate) => (candidate.id && ids.includes(candidate.id)) || (candidate.type && type && norm(candidate.type) === type)
        );
        (rule ? groups.get(rule.key) : other).items.push(entry);
      });
      const shown = Array.from(groups.values())
        .filter((group) => group.items.length)
        .sort((a, b) => b.items.length - a.items.length || a.order - b.order);
      if (other.items.length) shown.push(other);
      const limit = DESKTOP.matches ? 8 : 4;
      const fragment = document.createDocumentFragment();
      shown.forEach((group, groupIndex) => {
        const section = el('section', 'sbc-group');
        if (shown.length > 1) {
          const headingId = `sbc-group-${state.fam}-${groupIndex}`;
          section.setAttribute('aria-labelledby', headingId);
          const head = el('div', 'sbc-group-head');
          const title = el('h2');
          title.id = headingId;
          title.textContent = group.t;
          const count = el('span', 'sbc-group-count');
          count.textContent = String(group.items.length);
          head.append(title, count);
          section.append(head);
        } else {
          section.setAttribute('aria-label', group.t);
        }
        const grid = el('div', 'sh-products sbc-grid');
        const context = { color: state.fam, list: group.t };
        group.items.forEach((entry, i) => {
          const card = buildCard(entry, cfg, Object.assign({ position: i + 1 }, context));
          if (i >= limit) card.hidden = true;
          grid.append(card);
        });
        section.append(grid);
        if (group.items.length > limit) {
          const more = el('button', 'sh-button sh-button--outline sbc-more');
          more.type = 'button';
          more.textContent = cfg.s.show_all.replace('{n}', String(group.items.length));
          more.addEventListener('click', () => {
            grid.querySelectorAll('.sbc-card[hidden]').forEach((card) => {
              card.hidden = false;
            });
            more.remove();
            const next = grid.children[limit];
            if (next) {
              const link = next.querySelector('h3 a');
              if (link) link.focus({ preventScroll: true });
            }
            track('show_all', context);
          });
          section.append(more);
        }
        fragment.append(section);
      });
      results.replaceChildren(fragment);
    };

    const render = () => {
      if (!state.fam) {
        root.dataset.state = 'pick';
        summary.textContent = '';
        shadesBox.hidden = true;
        results.replaceChildren();
        document.documentElement.style.scrollPaddingTop = '';
        document.title = baseTitle;
        if (note) note.hidden = true;
        return;
      }
      root.dataset.state = 'results';
      renderNote();
      if (bar) document.documentElement.style.scrollPaddingTop = `${bar.offsetHeight + 16}px`;
      document.title = [familyName[state.fam], heading ? heading.textContent.trim() : '', cfg.shop].filter(Boolean).join(' | ');
      if (state.failed) {
        showError();
        return;
      }
      if (!state.index) {
        skeleton();
        return;
      }
      const all = state.index.get(state.fam) || [];
      renderShades(all);
      const list = state.shade ? all.filter((entry) => shadeOf(entry) === state.shade) : all;
      summary.replaceChildren();
      const name = el('strong');
      name.textContent = familyName[state.fam] || '';
      const count = el('span');
      count.textContent = countText(cfg, list.length);
      summary.append(name, count);
      if (!list.length) {
        renderEmpty();
        return;
      }
      renderGroups(list);
    };

    const choose = (fam, source) => {
      if (!isFamily(fam)) return;
      const changed = state.fam !== fam;
      state.fam = fam;
      if (changed) state.shade = '';
      setPressed();
      const url = new URL(window.location.href);
      url.searchParams.set('color', fam);
      if (changed) url.searchParams.delete('shade');
      window.history.replaceState(window.history.state, '', url);
      render();
      window.requestAnimationFrame(revealSelected);
      const top = results.getBoundingClientRect().top + window.scrollY - (bar ? bar.offsetHeight : 0) - 90;
      if (window.scrollY > top) window.scrollTo({ top, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      const available = state.index ? (state.index.get(fam) || []).length : null;
      track('color_select', { color: fam, source, available });
      if (source === 'empty_state') {
        const selected = buttons.find((button) => button.dataset.family === fam);
        if (selected) selected.focus({ preventScroll: true });
      }
    };

    const applyCounts = () => {
      buttons.forEach((button) => {
        const n = (state.index.get(button.dataset.family) || []).length;
        const badge = button.querySelector('.sbc-count');
        if (badge) {
          badge.textContent = String(n);
          badge.hidden = false;
        }
        button.hidden = n === 0 && button.dataset.family !== state.fam;
        button.setAttribute('aria-label', `${familyName[button.dataset.family]}: ${countText(cfg, n)}`);
      });
    };

    // Bring the chosen swatch into the rail's view without moving the page.
    const revealSelected = () => {
      const selected = buttons.find((button) => button.dataset.family === state.fam);
      const rail = root.querySelector('.sbc-picker');
      if (!selected || !rail || rail.scrollWidth <= rail.clientWidth) return;
      const railBox = rail.getBoundingClientRect();
      const box = selected.getBoundingClientRect();
      rail.scrollLeft += box.left + box.width / 2 - (railBox.left + railBox.width / 2);
    };

    async function load() {
      state.failed = false;
      render();
      try {
        const [items, colors] = await Promise.all([loadItems(cfg.source), loadColors(cfg.colors)]);
        state.colors = colors;
        state.index = buildIndex(items, colors, cfg);
        state.meanings = await loadColors(cfg.meanings).catch(() => null);
        applyCounts();
        window.requestAnimationFrame(revealSelected);
      } catch (error) {
        state.failed = true;
        if (window.console) console.error('[sbc]', error);
      }
      render();
    }

    buttons.forEach((button) => button.addEventListener('click', () => choose(button.dataset.family, 'color_page')));
    if (ask) ask.addEventListener('click', () => track('ask_whatsapp', { color: state.fam, source: 'color_page' }));
    const initial = new URLSearchParams(window.location.search).get('color');
    if (initial && isFamily(initial)) {
      state.fam = initial;
      state.shadeSlug = new URLSearchParams(window.location.search).get('shade') || '';
      setPressed();
    }
    load();
  };

  // ---------- homepage strip and collection bar ----------

  // The homepage row only links and tracks (no catalog download on the busiest page); the
  // collection bar reads this collection's stock to show counts and hide empty colors.
  const initSwatchLinks = (root, withCounts, source) => {
    const cfg = readJSON(root, 'script[data-sbc-config]');
    const links = Array.from(root.querySelectorAll('.sbc-sw'));
    links.forEach((link) => link.addEventListener('click', () => track('color_select', { color: link.dataset.family, source })));
    if (!withCounts) return;
    whenIdle(async () => {
      try {
        const [items, colors] = await Promise.all([loadItems(cfg.source), loadColors(cfg.colors)]);
        const index = buildIndex(items, colors, cfg);
        let shown = 0;
        links.forEach((link) => {
          const n = (index.get(link.dataset.family) || []).length;
          link.hidden = n === 0;
          if (n) shown += 1;
          const badge = link.querySelector('.sbc-count');
          if (badge) {
            badge.textContent = String(n);
            badge.hidden = false;
          }
        });
        if (!shown) root.hidden = true; // nothing in stock here: no empty labelled bar
        root.dataset.ready = 'true';
      } catch (error) {
        /* links still work without counts */
      }
    });
  };

  // ---------- product page: same color, other designs ----------

  const initSame = (root) => {
    const cfg = readJSON(root, 'script[data-sbc-config]');
    const me = readJSON(root, 'script[data-sbc-product]');
    const limit = Number(root.dataset.limit) || 8;
    const run = async () => {
      try {
        const [items, colors] = await Promise.all([loadItems(cfg.source), loadColors(cfg.colors)]);
        const resolve = makeResolver(colors, cfg.lang);
        const self = items.find((item) => item.i === me.i) || { i: me.i, c: me.c };
        const entries = resolve(self);
        const mine = entries.find((entry) => entry.variant && me.v && entry.variant === me.v) || entries[0];
        if (!mine) return;
        const index = buildIndex(items, colors, cfg);
        const list = (index.get(mine.fam) || []).filter((entry) => entry.p.i !== me.i && !entry.secondary).slice(0, limit);
        if (list.length < 2) return;
        const family = cfg.families.find((item) => item.k === mine.fam);
        const row = root.querySelector('.sbc-same-row');
        const context = { color: mine.fam, list: 'same_color' };
        row.replaceChildren(...list.map((entry, i) => buildCard(entry, cfg, Object.assign({ position: i + 1 }, context))));
        const dot = root.querySelector('.sbc-same-eyebrow .sbc-dot');
        if (dot) dot.classList.add(`sbc-dot--${mine.fam}`);
        const label = root.querySelector('[data-sbc-same-color]');
        if (label && family) label.textContent = family.n;
        const all = root.querySelector('[data-sbc-same-all]');
        if (all) {
          all.href = withQuery(cfg.all, { view: 'colors', color: mine.fam });
          all.addEventListener('click', () => track('color_select', { color: mine.fam, source: 'product_page' }));
        }
        root.hidden = false;
      } catch (error) {
        /* the block simply stays hidden */
      }
    };
    whenIdle(run);
  };

  // ---------- product page: about this color ----------

  const initAbout = (root) => {
    const cfg = readJSON(root, 'script[data-sbc-config]');
    const me = readJSON(root, 'script[data-sbc-product]');
    const familyName = Object.fromEntries(cfg.families.map((family) => [family.k, family.n]));
    const $ = (selector) => root.querySelector(selector);
    const setRow = (selector, value) => {
      const row = $(`[${selector}-row]`);
      const cell = $(`[${selector}]`);
      if (cell) cell.textContent = value || '';
      if (row) row.hidden = !value;
    };
    const currentVariant = () => {
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      return Number((input && input.value) || fromUrl || me.v) || me.v;
    };
    let colors = null;
    let meanings = null;
    let noral = null;
    const render = () => {
      const resolve = makeResolver(colors, cfg.lang);
      const entries = resolve(me);
      const variant = currentVariant();
      // A listing whose variants all share one family (photo-position options such as «وسط TL-08») keeps one entry per family,
      // so when the entries do not carry the chosen variant, use that variant's own photo-verified colour (colors.variants).
      const exact = (colors.variants || {})[variant];
      const mine = entries.find((entry) => entry.variant && entry.variant === variant)
        || (exact ? { fam: exact[0], shade: cfg.lang === 'ar' ? exact[1] : exact[2], slug: exact[3], variant } : null)
        || entries[0];
      if (!mine) return;
      const known = (colors.products || {})[me.i];
      const shade = mine.shade || familyName[mine.fam] || '';
      const group = paletteOf(colors, mine);
      const copy = meaningOf(meanings, mine.fam, group, cfg.lang) || {};
      const dot = $('[data-sbc-about-dot]');
      dot.className = `sbc-dot sbc-dot--${mine.fam}`;
      $('[data-sbc-about-shade]').textContent = shade;
      const text = $('[data-sbc-about-text]');
      text.textContent = copy.about || '';
      text.hidden = !copy.about;
      setRow('data-sbc-about-meaning', copy.meaning);
      setRow('data-sbc-about-wear', copy.wear);
      const pairs = $('[data-sbc-about-pairs]');
      pairs.replaceChildren(
        ...(copy.pairs || []).filter((fam) => familyName[fam]).map((fam) => {
          const link = el('a', 'sbc-chip sbc-chip--link');
          link.href = withQuery(cfg.all, { view: 'colors', color: fam });
          const swatch = el('span', `sbc-dot sbc-dot--sm sbc-dot--${fam}`);
          swatch.setAttribute('aria-hidden', 'true');
          link.append(swatch, document.createTextNode(familyName[fam]));
          link.addEventListener('click', () => track('color_select', { color: fam, source: 'product_pairs' }));
          return link;
        })
      );
      $('[data-sbc-about-pairs-row]').hidden = !pairs.children.length;
      const guide = $('[data-sbc-about-guide]');
      if (guide) guide.href = withQuery(cfg.all, { view: 'color-guide', color: mine.fam });
      const shop = $('[data-sbc-about-shop]');
      shop.href = withQuery(cfg.all, { view: 'colors', color: mine.fam });
      shop.textContent = fill(cfg.s.about_shop, { color: familyName[mine.fam] || '' });
      const productUrl = new URL(me.u, window.location.origin);
      if (variant) productUrl.searchParams.set('variant', String(variant));
      const askLink = $('[data-sbc-ask]');
      if (askLink) askLink.href = waHref(cfg, `${fill(cfg.s.ask_text_product, { color: shade, title: me.t })}\n${productUrl.href}`);
      // Sold out (the chosen variant): offer the back-in-stock request, tagged with the color.
      const soldOut = me.a ? me.a[String(variant)] === false : false;
      const notify = $('[data-sbc-about-notify]');
      const posted = /[?&]customer_posted=true/.test(window.location.search) && notify.querySelector('.sbc-notify-done');
      notify.hidden = !soldOut && !posted;
      const tags = notify.querySelector('[data-sbc-notify-tags]');
      if (tags) tags.value = `${tags.dataset.base},sbc-notify-color-${mine.fam}${variant ? `,sbc-notify-variant-${variant}` : ''}`;
      root.dataset.color = mine.fam;
      renderNoral(mine.fam, copy.pairs || []);
      root.hidden = false;
    };
    // «نسقيها مع جلابية من نورال»: in-stock Noral jalabiyas (photo-verified snapshot, assets/sbc-noral.json) whose
    // ground colour goes with this shayla, whose print carries its colour, or that share it; one per design.
    const renderNoral = (fam, pairs) => {
      const box = $('[data-sbc-noral]');
      if (!box || !noral || cfg.category !== 'shayla') return;
      const scored = (noral.items || []).map((item) => {
        let score = 0, why = '';
        if (pairs.includes(item.g)) { score = 3; why = cfg.s.noral_harmony; }
        else if ((item.a || []).includes(fam)) { score = 2; why = cfg.s.noral_echo; }
        else if (item.g === fam) { score = 1; why = cfg.s.noral_tone; }
        return { item, score: score + (item.c === 'high' ? 0.1 : 0), why };
      }).filter((row) => row.score >= 1).sort((a, b) => b.score - a.score);
      const seen = new Set(), picks = [];
      scored.forEach((row) => { if (picks.length < 4 && !seen.has(row.item.h)) { seen.add(row.item.h); picks.push(row); } });
      const list = $('[data-sbc-noral-row]');
      list.replaceChildren(...picks.map(({ item, why }) => {
        const card = el('a', 'sbc-noral-card');
        card.href = `${noral.store}/products/${item.h}?variant=${item.v}&utm_source=shailah&utm_medium=referral&utm_campaign=color_match&utm_content=${fam}`;
        card.target = '_blank';
        card.rel = 'noopener';
        const media = el('span', 'sbc-noral-media');
        const img = el('img');
        img.src = item.img + (item.img.includes('?') ? '&' : '?') + 'width=360';
        img.alt = cfg.lang === 'ar' ? item.ar : item.en;
        img.loading = 'lazy';
        media.append(img);
        const reason = el('span', 'sbc-noral-why');
        reason.textContent = why;
        const name = el('span', 'sbc-noral-name');
        name.textContent = cfg.lang === 'ar' ? item.ar : item.en;
        const title = el('span', 'sbc-noral-design');
        title.textContent = item.t;
        const go = el('span', 'sbc-noral-go');
        go.textContent = cfg.s.noral_go;
        card.append(media, reason, name, title, go);
        card.addEventListener('click', () => track('noral_click', { color: fam, noral_variant: item.v }));
        return card;
      }));
      box.hidden = !picks.length;
    };
    const run = async () => {
      try {
        [colors, meanings, noral] = await Promise.all([loadColors(cfg.colors), loadColors(cfg.meanings).catch(() => null), loadColors(cfg.noral).catch(() => null)]);
        render();
      } catch (error) {
        /* the block simply stays hidden */
      }
    };
    const askButton = $('[data-sbc-ask]');
    if (askButton) askButton.addEventListener('click', () => track('ask_whatsapp', { color: root.dataset.color || '', source: 'product_page', product_id: me.i }));
    const notifyForm = root.querySelector('form');
    if (notifyForm) notifyForm.addEventListener('submit', () => track('notify_request', { color: root.dataset.color || '', source: 'product_page', product_id: me.i }));
    // The theme's variant picker updates the cart form's id input; follow it.
    document.addEventListener('change', (event) => {
      if (colors && event.target && (event.target.name === 'id' || event.target.closest('variant-radios, variant-selects, .product-form, form[action*="/cart/add"]'))) {
        window.setTimeout(render, 0);
      }
    });
    whenIdle(run, 1200);
  };

  // ---------- colours guide ----------

  const initGuide = (root) => {
    const items = Array.from(root.querySelectorAll('details.sbc-guide-item'));
    items.forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      items.forEach((other) => { if (other !== item) other.open = false; });
      const url = new URL(window.location.href);
      url.searchParams.set('color', item.dataset.family);
      window.history.replaceState(window.history.state, '', url);
      track('guide_open', { color: item.dataset.family });
    }));
    const initial = new URLSearchParams(window.location.search).get('color');
    const target = items.find((item) => item.dataset.family === initial);
    if (target) { target.open = true; target.scrollIntoView({ block: 'start' }); }
  };

  // ---------- boot ----------

  const init = (scope) => {
    scope.querySelectorAll('[data-sbc-view]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initView(root);
    });
    scope.querySelectorAll('[data-sbc-strip]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initSwatchLinks(root, false, 'home');
    });
    scope.querySelectorAll('[data-sbc-bar]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initSwatchLinks(root, true, 'collection_bar');
    });
    scope.querySelectorAll('[data-sbc-guide]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initGuide(root);
    });
    scope.querySelectorAll('[data-sbc-about]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initAbout(root);
    });
    scope.querySelectorAll('[data-sbc-same]:not([data-sbc-ready])').forEach((root) => {
      root.dataset.sbcReady = '1';
      initSame(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
